// Справка „Наличности" — снимка на един склад към момента (без период) или
// „Към дата" — към края на избран минал ден (`GET /inventory/stock?asOf=`):
// количествата са преизчислени по документите, активни СЕГА, а стойностите са
// по ТЕКУЩИТЕ цени — така пише и над таблицата, и в Excel. Хедър „Справка" с
// общата лента (складът и датата на мястото на периода · Excel · Опресни), KPI
// за целия склад, търсачка
// (продуктът се търси тук) и „Скрий без наличност", филтри В ЗАГЛАВИЯТА НА
// КОЛОНИТЕ (Група · Наличност под/до/над минимума) с ред „Филтри: …" над
// таблицата, таблица (карти на телефон) с „Покажи още" и ред „Общо".
// Клик/Enter на ред отваря складовата карта на продукта (`stockCard/`): на
// десктоп вдясно, а таблицата става тесен списък (↑/↓ сменя продукта, Esc
// затваря); под 1024 px — лист на цял екран. Складът се избира В страницата
// (по подразбиране — този от конфигурацията). При минала дата картата се
// отваря с последните 30 дни ДО нея и партидите към нея.
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { DatePicker, Select } from "antd";
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  EuroOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import {
  useProducts,
  useStockBalances,
  useInventorySettings,
  useWarehouses,
} from "../../../hooks/useInventory";
import { exportToExcel } from "../../../utils/excelExport";
import { InventoryTheme } from "../components/InventoryTheme";
import { useInventoryShell } from "../components/shell/InventoryShellContext";
import { PageHeader } from "../components/list/PageHeader";
import { EmptyScreen, KpiGrid, SearchBox, SectionCard, type KpiCard } from "../components/list/ListBits";
import { ReportToolbar } from "../components/list/ReportToolbar";
import { refreshOf } from "../components/list/reportRefresh";
import { CheckBox } from "../components/filters/CheckBox";
import { FilterSummaryLine } from "../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../components/filters/useColumnFilters";
import { useShowMore } from "../components/list/useShowMore";
import { formatMoney } from "../components/numbers";
import type { Period } from "../components/period/periodPresets";
import {
  EMPTY_STOCK_FILTERS,
  buildStockRows,
  searchStockRows,
  sortStockRows,
  stockFilterSpecs,
  stockKpis,
  stockQtyLayout,
  stockSortFirstDir,
  stockTotals,
  type StockFilterKey,
  type StockSort,
  type StockSortKey,
} from "./components/stockShared";
import { nextSort, type LegacyReportProps } from "./components/reportListModel";
import { ReportLoading, ReportNoMatch, ResultSum, ShowMoreRow } from "./components/ReportListBits";
import { StockReportTable, type StockTableDensity } from "./components/StockReportTable";
import { StockCardPanel } from "./stockCard/StockCardPanel";
import { StockCardMasterList } from "./stockCard/StockCardMasterList";
import { focusStockCard, useStockCardSelection } from "./stockCard/useStockCardSelection";
import { useStockSubstitutionSummary } from "./stockCard/useStockSubstitutionSummary";

export const StockReport: FC<LegacyReportProps> = () => {
  const { t } = useTranslation();
  const { warehouseId } = useInventoryShell();
  const { isNarrow, isWide, isDesktop } = useViewport();

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<StockSort | null>(null);
  // Филтър-изглед; не се помни между отварянията.
  const [hideZeroStock, setHideZeroStock] = useState(false);
  // Складът се избира в самата страница (по подразбиране този от конфигурацията).
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(warehouseId);
  useEffect(() => {
    if (warehouseId != null) setSelectedWarehouseId((current) => current ?? warehouseId);
  }, [warehouseId]);
  // „Към дата": минал ден или null (= сега). Днешният ден е „сега" — полето
  // показва празно „Към днес" вместо дата, за която не пише нищо.
  const [asOfDate, setAsOfDate] = useState<Dayjs | null>(null);
  const asOfDay = asOfDate ? asOfDate.format("YYYY-MM-DD") : null;
  const asOfLabel = asOfDate ? asOfDate.format("DD.MM.YYYY") : "";
  const onAsOfChange = (value: Dayjs | null) =>
    setAsOfDate(value && value.isBefore(dayjs(), "day") ? value.startOf("day") : null);
  // Картата при минала дата: последните 30 дни до нея (не до днес)
  const cardPeriod = useMemo<Period | undefined>(
    () => (asOfDate ? { from: asOfDate.subtract(29, "day"), to: asOfDate } : undefined),
    [asOfDate],
  );

  const { data: warehouses = [] } = useWarehouses();
  const productsQuery = useProducts();
  const { data: products = [], isLoading: productsLoading } = productsQuery;
  const { data: settings } = useInventorySettings();
  const vatRegistered = settings?.vatRegistered ?? true;
  const balancesQuery = useStockBalances(selectedWarehouseId, asOfDay);
  const { data: balances = [], isLoading: balancesLoading, isFetching } = balancesQuery;
  const { refresh, refreshing } = refreshOf(balancesQuery, productsQuery);
  const isLoading = productsLoading || balancesLoading;

  // Подредбата е в `buildStockRows`: първо продуктите с наличност, после
  // нулевите — и двете групи по азбучен ред.
  const allRows = useMemo(
    () => buildStockRows(products, new Map(balances.map((b) => [b.productId, b])), vatRegistered),
    [products, balances, vatRegistered],
  );
  // ─── Филтри в колоните (броячите — фасетни, по склада след търсенето) ───
  const specs = useMemo(
    () =>
      stockFilterSpecs({
        titles: { groups: t("inventoryPage.colGroup"), buckets: t("inventoryPage.colAvailable") },
        bucketLabel: (bucket) =>
          ({
            under: t("inventoryPage.underMin"),
            near: t("inventoryPage.nearMin"),
            over: t("inventoryPage.overMin"),
          })[bucket] ?? bucket,
      }),
    [t],
  );
  const searched = useMemo(
    () => searchStockRows(allRows, query, hideZeroStock),
    [allRows, query, hideZeroStock],
  );
  const filters = useColumnFilters({ specs, empty: EMPTY_STOCK_FILTERS, universe: allRows, base: searched });
  const shown = useMemo(() => sortStockRows(filters.rows, sort), [filters.rows, sort]);
  // Низ, не масив — useShowMore нулира при СМЯНА на ключа
  const resetKey = JSON.stringify([selectedWarehouseId, asOfDay, filters.state, query, hideZeroStock, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, resetKey);
  const kpis = stockKpis(allRows);
  const totals = stockTotals(shown);
  // За целия филтриран резултат — „Покажи още" не мести запетаите
  const qtyLayout = useMemo(() => stockQtyLayout(shown), [shown]);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
    setHideZeroStock(false);
  };
  const sections: ColumnSections<StockFilterKey> = {
    groups: filters.section("groups"),
    buckets: filters.section("buckets"),
  };
  // Складовата карта: избраният продукт, Esc и фокусът обратно на реда
  const tableRef = useRef<HTMLDivElement | null>(null);
  const card = useStockCardSelection({ allRows, visibleRows: visible, tableRef });
  const substitutions = useStockSubstitutionSummary(selectedWarehouseId, cardPeriod);
  const cardRow = selectedWarehouseId != null ? card.selected : null;
  const paneOpen = !!cardRow && isDesktop;


  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.kpiProducts"), value: String(kpis.total), Icon: AppstoreOutlined },
    { label: t("inventoryPage.underMin"), value: String(kpis.under), Icon: ArrowDownOutlined, tone: "danger" },
    { label: t("inventoryPage.nearMin"), value: String(kpis.near), Icon: WarningOutlined },
    { label: t("inventoryPage.overMin"), value: String(kpis.over), Icon: CheckCircleOutlined },
    { label: t("inventoryPage.kpiValueGross"), value: formatMoney(kpis.gross), Icon: EuroOutlined, tone: "brand" },
  ];

  const warehouseName = warehouses.find((w) => w.id === selectedWarehouseId)?.name ?? "";
  const asOfNote = asOfDay ? t("inventoryPage.stockAsOfNote", { date: asOfLabel }) : null;
  const exportXls = () =>
    exportToExcel({
      filename: asOfDay ? `nalichnosti_kam_${asOfDay}` : "nalichnosti",
      sheetName: `${t("inventoryPage.stockReport")} ${warehouseName}`.trim(),
      notes: asOfNote ? [`${warehouseName} · ${asOfNote}`] : undefined,
      rows: shown,
      columns: [
        { header: t("inventoryPage.colProductName"), value: (row) => row.name },
        { header: t("inventoryPage.colGroup"), value: (row) => row.category ?? "" },
        { header: t("inventoryPage.colUnit"), value: (row) => row.unit ?? "" },
        { header: t("inventoryPage.colMinQty"), value: (row) => row.minQty },
        { header: t("inventoryPage.colAvgPrice"), value: (row) => row.weightedAvgPrice },
        { header: t("inventoryPage.colLastPrice"), value: (row) => row.lastDeliveryPrice },
        { header: t("inventoryPage.colAvailable"), value: (row) => row.quantity },
        { header: t("inventoryPage.colNet"), value: (row) => Number(row.sumNoVat.toFixed(2)) },
        { header: t("inventoryPage.colGross"), value: (row) => Number(row.sumWithVat.toFixed(2)) },
      ],
    });

  const density: StockTableDensity = isNarrow ? "cards" : isWide ? "full" : "medium";
  const onSort = (key: StockSortKey) => setSort((current) => nextSort(current, key, stockSortFirstDir(key)));

  // Бутонът-превключвател „Скрий без наличност" — във вида на бутоните-филтри
  const hideZeroToggle = (
    <button
      type="button"
      onClick={() => setHideZeroStock((on) => !on)}
      aria-pressed={hideZeroStock}
      className={`flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[9px] border px-3 py-2 text-[13.5px] leading-[normal] text-ink outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
        hideZeroStock ? "border-brand/45 bg-brand-tint" : "border-shade-3 bg-surface hover:border-shade-5"
      }`}
    >
      <CheckBox checked={hideZeroStock} />
      {t("inventoryPage.hideZeroStock")}
    </button>
  );

  return (
    <InventoryTheme>
      <div className="relative flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.stockReport")}
          subtitle={
            selectedWarehouseId != null && !isLoading
              ? t("inventoryPage.stockSubtitle", { count: kpis.total, inStock: kpis.inStock })
              : undefined
          }
          right={
            <ReportToolbar
              lead={
                <>
                  <Select
                    value={selectedWarehouseId ?? undefined}
                    onChange={(value) => setSelectedWarehouseId(value as number)}
                    placeholder={t("inventoryPage.fieldWarehouse")}
                    options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                    aria-label={t("inventoryPage.fieldWarehouse")}
                    className={`!h-[38px] [&_.ant-select-selector]:!h-[38px] [&_.ant-select-selector]:!rounded-lg ${
                      isNarrow ? "min-w-0 flex-1" : "!w-[200px]"
                    }`}
                  />
                  {/* „Към дата": празно = сега; бъдещи дни няма */}
                  <DatePicker
                    value={asOfDate}
                    onChange={onAsOfChange}
                    format="DD.MM.YYYY"
                    placeholder={t("inventoryPage.stockAsOfNow")}
                    disabledDate={(current) => current.isAfter(dayjs(), "day")}
                    aria-label={t("inventoryPage.stockAsOfField")}
                    className="!h-[38px] !w-[140px] shrink-0 !rounded-lg"
                  />
                </>
              }
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              // Картата на цял екран (под 1024 px) е „отгоре" — Ctrl+E не бива
              // да изнася списъка под нея
              exportShortcut={!(cardRow && !isDesktop)}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div className="flex min-h-0 flex-1">
        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : paneOpen ? "px-5 pb-12 pt-5" : "px-7 pb-12 pt-5"
          }`}
        >
          {selectedWarehouseId == null ? (
            <SectionCard>
              <EmptyScreen Icon={InboxOutlined} tone="brand" title={t("inventoryPage.stockPickWarehouse")} />
            </SectionCard>
          ) : (
            <>
              {/* Докато наличностите се зареждат, всички продукти изглеждат с 0 —
                  KPI-тата чакат данните, за да не подвеждат */}
              {asOfNote && (
                <div
                  role="note"
                  className="flex items-start gap-2 rounded-xl border border-info/30 bg-info-tint px-4 py-2.5 text-[13px] leading-[1.45] text-info-deep"
                >
                  <InfoCircleOutlined aria-hidden className="mt-[3px] shrink-0" />
                  <span>{asOfNote}</span>
                </div>
              )}
              {!isLoading && !paneOpen && <KpiGrid cards={kpiCards} />}
              <div className="flex flex-wrap items-center gap-2">
                <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.searchStockPh")} />
                {hideZeroToggle}
              </div>
              <SectionCard className={isFetching && !isLoading ? "opacity-60" : ""}>
                <FilterSummaryLine
                  countLabel={t("inventoryPage.reportResult", { count: shown.length })}
                  items={filters.summary}
                  onClearAll={filters.clearAll}
                >
                  <ResultSum label={t("inventoryPage.colNet")} value={formatMoney(totals.net)} />
                  <ResultSum label={t("inventoryPage.colGross")} value={formatMoney(totals.gross)} strong />
                </FilterSummaryLine>
                {isLoading ? (
                  <ReportLoading />
                ) : allRows.length === 0 ? (
                  <EmptyScreen Icon={AppstoreOutlined} tone="brand" title={t("inventoryPage.noProducts")} />
                ) : paneOpen && cardRow ? (
                  // Картата е отворена: таблицата отстъпва на тесния списък
                  <StockCardMasterList
                    rows={visible}
                    selectedId={cardRow.productId}
                    onSelect={card.select}
                    onEnterCard={focusStockCard}
                    substitutions={substitutions.byProduct}
                    substitutionPeriod={substitutions.period}
                    footer={<ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />}
                  />
                ) : (
                  <div ref={tableRef}>
                    <StockReportTable
                      rows={visible}
                      density={density}
                      sort={sort}
                      onSort={onSort}
                      filters={sections}
                      onOpen={card.open}
                      totals={totals}
                      qtyLayout={qtyLayout}
                      substitutions={substitutions.byProduct}
                      substitutionPeriod={substitutions.period}
                      footer={
                        shown.length ? (
                          <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                        ) : (
                          <ReportNoMatch onClear={clearAll} />
                        )
                      }
                    />
                  </div>
                )}
              </SectionCard>
            </>
          )}
        </div>
        {paneOpen && cardRow && selectedWarehouseId != null && (
          <aside className="flex w-[48%] min-w-[440px] max-w-[820px] shrink-0 flex-col">
            <StockCardPanel
              // нова дата → картата наново (периодът ѝ свършва на датата)
              key={asOfDay ?? "now"}
              row={cardRow}
              warehouseId={selectedWarehouseId}
              warehouseName={warehouseName}
              variant="pane"
              onClose={card.close}
              initialPeriod={cardPeriod}
              asOfDay={asOfDay}
            />
          </aside>
        )}
        </div>

        {/* Под 1024 px картата е лист на цял екран; справката остава отдолу */}
        {cardRow && !isDesktop && selectedWarehouseId != null && (
          <div className="absolute inset-0 z-30 flex flex-col bg-canvas">
            <StockCardPanel
              // нова дата → картата наново (периодът ѝ свършва на датата)
              key={asOfDay ?? "now"}
              row={cardRow}
              warehouseId={selectedWarehouseId}
              warehouseName={warehouseName}
              variant="sheet"
              onClose={card.close}
              initialPeriod={cardPeriod}
              asOfDay={asOfDay}
            />
          </div>
        )}
      </div>
    </InventoryTheme>
  );
};

export default StockReport;
